import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../../../shared/src/services/api/ApiService';

interface WechatBindingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const WechatBindingModal: React.FC<WechatBindingModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'success' | 'expired' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 生成关联二维码
  const generateBindingQRCode = async () => {
    try {
      setStatus('loading');
      setErrorMessage('');

      const response = await apiService.post('/auth/wechat/binding/generate', {});

      if (response.success && response.data) {
        setQrCodeUrl(response.data.qrCodeUrl);
        setSessionId(response.data.sessionId);
        setStatus('ready');

        // 开始轮询状态
        startPolling(response.data.sessionId);
      } else {
        throw new Error(response.error?.message || '生成二维码失败');
      }
    } catch (error: any) {
      console.error('生成关联二维码失败:', error);
      setErrorMessage(error.message || '生成二维码失败');
      setStatus('error');
    }
  };

  // 轮询关联状态
  const startPolling = (sid: string) => {
    // 清除之前的轮询
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // 每2秒轮询一次
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await apiService.get(`/auth/wechat/binding/status/${sid}`);

        if (response.success && response.data) {
          const { status: bindingStatus } = response.data;

          if (bindingStatus === 'confirmed') {
            console.log('=== Binding confirmed, updating status ===');
            setStatus('success');
            stopPolling();

            // 立即刷新用户信息，然后延迟关闭弹窗
            (async () => {
              try {
                if (onSuccess) {
                  console.log('Calling onSuccess callback to refresh user');
                  await onSuccess();
                  console.log('onSuccess callback completed');
                }
              } catch (error) {
                console.error('Error in onSuccess callback:', error);
              }
              
              // 延迟关闭弹窗，让用户看到成功提示
              setTimeout(() => {
                console.log('Closing binding modal');
                onClose();
              }, 2000);
            })();
          } else if (bindingStatus === 'expired' || bindingStatus === 'cancelled') {
            setStatus('expired');
            stopPolling();
          }
        }
      } catch (error) {
        console.error('轮询关联状态失败:', error);
      }
    }, 2000);
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // 取消关联
  const handleCancel = async () => {
    if (sessionId) {
      try {
        await apiService.post('/auth/wechat/binding/cancel', { sessionId });
      } catch (error) {
        console.error('取消关联失败:', error);
      }
    }

    stopPolling();
    onClose();
  };

  // 重新生成二维码
  const handleRetry = () => {
    stopPolling();
    generateBindingQRCode();
  };

  // 组件挂载时生成二维码
  useEffect(() => {
    if (isOpen) {
      generateBindingQRCode();
    }

    return () => {
      stopPolling();
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
        {/* 关闭按钮 */}
        <button
          onClick={handleCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* 标题 */}
        <h2 className="text-2xl font-bold text-center mb-6">关联微信账号</h2>

        {/* 内容区域 */}
        <div className="flex flex-col items-center">
          {status === 'loading' && (
            <div className="flex flex-col items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">生成二维码中...</p>
            </div>
          )}

          {status === 'ready' && (
            <>
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200 mb-4">
                <img
                  src={qrCodeUrl}
                  alt="关联二维码"
                  className="w-64 h-64"
                />
              </div>
              <p className="text-center text-gray-600 mb-2">
                请使用微信扫描二维码
              </p>
              <p className="text-center text-sm text-gray-500">
                扫码后在小程序中确认关联
              </p>
            </>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xl font-semibold text-green-600 mb-2">关联成功！</p>
              <p className="text-gray-600">您现在可以使用微信登录了</p>
            </div>
          )}

          {status === 'expired' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-xl font-semibold text-yellow-600 mb-2">二维码已过期</p>
              <p className="text-gray-600 mb-4">请重新生成二维码</p>
              <button
                onClick={handleRetry}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                重新生成
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-xl font-semibold text-red-600 mb-2">生成失败</p>
              <p className="text-gray-600 mb-4">{errorMessage || '请稍后重试'}</p>
              <button
                onClick={handleRetry}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                重试
              </button>
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        {(status === 'ready' || status === 'loading') && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WechatBindingModal;
